import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { MoreHoriz } from '@mui/icons-material';

import { DialogCloseButton } from '../../../components/core/DialogCloseButton';
import { AuditLogActionChip } from './AuditLogActionChip';
import type { AuditLogEntry, JSONValue } from '../../../store/modules/merchant/types';
import { getAuditLogResourceLabel } from '../resourceLabels';
import {
  getPortalUserAccessSubject,
  getPortalUserAccessSubjectName,
} from '../auditLogPortalUserAccess';

type Props = {
  auditLog: AuditLogEntry;
};

const formatJsonValue = (value: JSONValue | null) => {
  if (value == null) return 'N/A';
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
};

const DetailField = ({
  label,
  children,
  xs = 12,
  md = 12,
}: {
  label: string;
  children: React.ReactNode;
  xs?: number;
  md?: number;
}) => (
  <Grid item xs={xs} md={md}>
    <Typography variant="small" fontWeight="semibold" color="grey.600">
      {label}
    </Typography>
    {typeof children === 'string' || typeof children === 'number' ? (
      <Typography variant="medium" sx={{ wordBreak: 'break-word' }}>
        {children}
      </Typography>
    ) : (
      children
    )}
  </Grid>
);

const DetailSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box>
    <Typography variant="large" fontWeight="semibold" color="text.primary" mb={1.5}>
      {title}
    </Typography>
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

const SnapshotBlock = ({ label, value }: { label: string; value: JSONValue | null }) => (
  <Box>
    <Typography variant="large" fontWeight="semibold" color="text.primary" mb={1.5}>
      {label}
    </Typography>
    <Box
      component="pre"
      sx={{
        backgroundColor: 'grey.100',
        border: '2px solid',
        borderColor: 'grey.400',
        borderRadius: 2,
        px: 2,
        py: 1.5,
        fontSize: 12,
        fontFamily: 'monospace',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        m: 0,
      }}
    >
      <code>{formatJsonValue(value)}</code>
    </Box>
  </Box>
);

export const AuditLogDetails = ({ auditLog }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const isPortalUserAccess = auditLog.resource === 'portalUserAccess';
  const accessSubject = isPortalUserAccess ? getPortalUserAccessSubject(auditLog) : null;
  const showsJsonSnapshot = !isPortalUserAccess;

  return (
    <>
      <IconButton
        size="small"
        aria-label="View audit log details"
        onClick={() => setIsOpen(true)}
        sx={{ color: 'grey.700' }}
      >
        <MoreHoriz fontSize="small" />
      </IconButton>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth={showsJsonSnapshot ? 'md' : 'sm'}
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          Audit log details
          <DialogCloseButton onClose={() => setIsOpen(false)} />
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <DetailSection title="Event">
              <DetailField label="Action">
                <Box mt={0.5}>
                  <AuditLogActionChip
                    action={auditLog.action}
                    resource={auditLog.resource}
                    size="small"
                  />
                </Box>
              </DetailField>
              <DetailField label="Resource">
                {getAuditLogResourceLabel(auditLog.resource)}
              </DetailField>
              <DetailField label="Timestamp">
                {dayjs(auditLog.timestamp).format('MM/DD/YY hh:mm:ss A')}
              </DetailField>
            </DetailSection>

            <DetailSection title="Performed by">
              <DetailField label="Email">{auditLog.user || '—'}</DetailField>
            </DetailSection>

            {isPortalUserAccess && (
              <DetailSection title="Portal user">
                <DetailField label="Name" md={6}>
                  {getPortalUserAccessSubjectName(accessSubject) || '—'}
                </DetailField>
                <DetailField label="Email" md={6}>
                  {accessSubject?.email || '—'}
                </DetailField>
              </DetailSection>
            )}

            {!isPortalUserAccess && auditLog.action === 'update' && (
              <>
                <SnapshotBlock label="Previous Value" value={auditLog.previousValue} />
                <SnapshotBlock label="New Value" value={auditLog.newValue} />
              </>
            )}
            {!isPortalUserAccess && auditLog.action === 'create' && (
              <SnapshotBlock label="New Value" value={auditLog.newValue} />
            )}
            {!isPortalUserAccess && auditLog.action === 'delete' && (
              <SnapshotBlock label="Previous Value" value={auditLog.previousValue} />
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};
